import { type CSSProperties, type SyntheticEvent, useEffect, useState } from 'react';

/**
 * Pomocniki ruchu. Same klatki i progi mieszkają w `styles.css`; tu jest tylko
 * to, czego CSS nie wie sam: który z kolei jest element i czy obraz już doszedł.
 */

/**
 * Indeks elementu dla `enter-rise` — z niego CSS liczy opóźnienie wejścia.
 *
 * Zmienna, a nie `animation-delay` w stylu: sufit opóźnienia i wyłączenie przy
 * ograniczonym ruchu trzyma arkusz, w jednym miejscu.
 */
export function stagger(index: number): CSSProperties {
  return { '--i': index } as CSSProperties;
}

function markLoaded(img: HTMLImageElement): void {
  img.dataset.loaded = '';
}

/**
 * Łagodne pojawienie się obrazu po wczytaniu: `<img {...fadeIn} />`.
 *
 * Znacznik stawiamy wprost na elemencie, nie w stanie Reacta — kafli bywa
 * sto na ekranie i każdy przerysowywałby się po swoim obrazie. `ref` łapie obraz
 * już gotowy z pamięci podręcznej, bo `load` mógł przyjść przed podpięciem
 * obsługi. Błąd też odsłania element: niewidoczny na zawsze byłby gorszy od pustego.
 */
export const fadeIn = {
  'data-fade': '',
  ref: (img: HTMLImageElement | null): void => {
    if (img?.complete === true) markLoaded(img);
  },
  onLoad: (event: SyntheticEvent<HTMLImageElement>): void => markLoaded(event.currentTarget),
  onError: (event: SyntheticEvent<HTMLImageElement>): void => markLoaded(event.currentTarget),
};

/** Po ilu milisekundach czekania wskaźnik tłumaczy, czemu to trwa. */
export const SLOW_WAIT_MS = 6000;

/**
 * Czy od `since` minęło już `ms`. Liczone od podanej chwili, a nie od
 * zamontowania: ekran, który podmienia szkielet na gotową treść, montuje
 * wskaźnik drugi raz, a licznik od zera kazałby czekającemu czytać od nowa
 * pierwsze zdanie.
 */
export function useWaitedLong(since: number, ms = SLOW_WAIT_MS): boolean {
  const [long, setLong] = useState(() => Date.now() - since >= ms);

  useEffect(() => {
    const remaining = ms - (Date.now() - since);
    if (remaining <= 0) {
      setLong(true);
      return;
    }
    setLong(false);
    const timer = setTimeout(() => setLong(true), remaining);
    return () => clearTimeout(timer);
  }, [since, ms]);

  return long;
}
